import type { Moneda } from '@/shared/billing/currency';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateMpPreferenceAction } from '@/application/actions';
import { httpClient } from '@/infrastructure/http/client/httpClient';

export type ContactTarget = {
  tenantId: string | null;
  clinicaId: number | null;
  nombreClinica: string;
  /** Nombre de la persona / dueño (si no hay, se usa la clínica). */
  nombreContacto: string | null;
  /** Nombre del plan (Base, Avanzado, etc.). */
  planName: string | null;
  emailAdministrador: string | null;
  telefonoContacto: string | null;
  periodo: string;
  totalAdeudado: number;
  /** Moneda de la deuda; en dólares no se genera link de Mercado Pago. */
  moneda?: Moneda;
};

type ContactChannel = 'email' | 'whatsapp';

export type PanelContactHistoryItem = {
  id: string;
  channel: ContactChannel;
  periodo: string | null;
  recipient: string;
  subjectOrConcepto: string | null;
  deliveryStatus: string;
  sentByUsername: string | null;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
  deliveryUpdatedAt: string | null;
};

interface ContactDebtorModalProps {
  isOpen: boolean;
  debtor: ContactTarget;
  onClose: () => void;
  onContactSaved?: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  sent: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  read: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  opened: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  clicked: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  bounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  dropped: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  responded: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  processed: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
};

/** Día N del mes del período (cierre de ventana de pago). */
/** Mes vencido: el período AAAA-MM vence el día de cierre del mes siguiente. */
function dueDateFromPeriod(periodo: string, closeDay = 10): Date | null {
  const [y, m] = periodo.split('-').map(Number);
  if (!y || !m) return null;
  const day = Math.min(28, Math.max(1, closeDay));
  return new Date(y, m, day);
}

function formatDueDate(periodo: string, locale: string, closeDay = 10): string {
  const d = dueDateFromPeriod(periodo, closeDay);
  if (!d) return periodo;
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Normaliza a dígitos internacionales para wa.me (AR: 549…). */
function normalizeWhatsAppPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 8) return null;
  if (digits.startsWith('54')) return digits;
  if (digits.startsWith('0')) return `54${digits.replace(/^0+/, '')}`;
  if (digits.length === 10) return `54${digits}`;
  return digits;
}

const LINK_PLACEHOLDER = '[link de Mercado Pago]';

const MESES_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

/** Ej: suscripción Coofit Base (Clínica Demo) Junio 2026 */
function formatSuscripcionConcepto(
  clinicName: string,
  periodo: string,
  planName?: string | null
): string {
  const [yStr, mStr] = periodo.split('-');
  const year = Number(yStr);
  const month = Number(mStr);
  const mesNombre =
    Number.isFinite(month) && month >= 1 && month <= 12 ? MESES_ES[month - 1] : periodo;
  const anio = Number.isFinite(year) ? String(year) : yStr || periodo;
  const clinic = clinicName.trim() || 'clínica';
  const plan = (planName || '').trim();
  if (plan) {
    return `suscripción Coofit ${plan} (${clinic}) ${mesNombre} ${anio}`;
  }
  return `suscripción Coofit (${clinic}) ${mesNombre} ${anio}`;
}

function buildWhatsAppPreview(params: {
  nombreCliente: string;
  concepto: string;
  monto: string;
  linkPago: string;
  contacto1: string;
  contacto2: string;
  telefono1: string;
  telefono2: string;
  dueDate: string;
}): string {
  return [
    `Hola ${params.nombreCliente}.`,
    ``,
    `Desde el equipo de Coofit te recordamos que tenés un saldo pendiente correspondiente a ${params.concepto}, por un importe de ${params.monto} a vencer el ${params.dueDate}.`,
    ``,
    `Si ya realizaste el pago, podés ignorar este mensaje.`,
    ``,
    `Si aún no abonaste, podés hacerlo desde el siguiente enlace:`,
    ``,
    params.linkPago,
    ``,
    `Si necesitás ayuda o tenés alguna consulta, podés comunicarte con nosotros:`,
    ``,
    `${params.contacto1}: 📞 ${params.telefono1}`,
    `${params.contacto2}: 📞 ${params.telefono2}`,
    ``,
    `Gracias por confiar en Coofit para la gestión de tu clínica.`,
    ``,
    `¡Muchas gracias!`,
  ].join('\n');
}

function buildEmailPreview(params: {
  nombreCliente: string;
  clinicName: string;
  periodo: string;
  dueDate: string;
  monto: string;
  linkPago: string;
}): string {
  return [
    `Hola ${params.nombreCliente},`,
    ``,
    `Te escribimos desde Coofit para recordarte el vencimiento del servicio de ${params.clinicName} correspondiente al período ${params.periodo} (vencimiento ${params.dueDate}).`,
    ``,
    `Monto adeudado: $ ${params.monto}.`,
    ``,
    `Podés abonar de forma segura con este link de Mercado Pago:`,
    params.linkPago,
    ``,
    `Ante cualquier consulta, quedamos a disposición.`,
    ``,
    `Equipo Coofit`,
  ].join('\n');
}

export const ContactDebtorModal: React.FC<ContactDebtorModalProps> = ({
  isOpen,
  debtor,
  onClose,
  onContactSaved,
}) => {
  const { t, i18n } = useTranslation();
  const { success, error: showError } = useToast();
  const [channel, setChannel] = useState<ContactChannel | null>(null);
  const [phone, setPhone] = useState(debtor.telefonoContacto || '');
  const [email, setEmail] = useState(debtor.emailAdministrador || '');
  const [contactName, setContactName] = useState(
    debtor.nombreContacto || debtor.nombreClinica || ''
  );
  const [isWorking, setIsWorking] = useState(false);
  const [panelPhone1, setPanelPhone1] = useState('');
  const [panelPhone2, setPanelPhone2] = useState('');
  const [panelContacto1, setPanelContacto1] = useState('');
  const [panelContacto2, setPanelContacto2] = useState('');
  const [paymentWindowCloseDay, setPaymentWindowCloseDay] = useState(10);
  const [history, setHistory] = useState<PanelContactHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!debtor.tenantId) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await httpClient.get<{ items: PanelContactHistoryItem[] }>(
        `/collections/contact-history?tenantId=${encodeURIComponent(debtor.tenantId)}&limit=30`
      );
      setHistory(res.data.items || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [debtor.tenantId]);

  useEffect(() => {
    if (!isOpen) return;
    setChannel(null);
    setPhone(debtor.telefonoContacto || '');
    setEmail(debtor.emailAdministrador || '');
    setContactName(debtor.nombreContacto || debtor.nombreClinica || '');

    void (async () => {
      try {
        const res = await httpClient.get<{
          telefonoContacto1: string | null;
          telefonoContacto2: string | null;
          contacto1: string | null;
          contacto2: string | null;
          ventanaPagoDiaCierre?: number;
        }>('/panel-config');
        setPanelPhone1(res.data.telefonoContacto1 || '');
        setPanelPhone2(res.data.telefonoContacto2 || '');
        setPanelContacto1(res.data.contacto1 || '');
        setPanelContacto2(res.data.contacto2 || '');
        if (res.data.ventanaPagoDiaCierre) {
          setPaymentWindowCloseDay(res.data.ventanaPagoDiaCierre);
        }
      } catch {
        setPanelPhone1('');
        setPanelPhone2('');
        setPanelContacto1('');
        setPanelContacto2('');
      }
    })();

    void loadHistory();
  }, [isOpen, debtor, loadHistory]);

  const locale = i18n.language?.startsWith('pt')
    ? 'pt-BR'
    : i18n.language?.startsWith('en')
      ? 'en-US'
      : 'es-AR';

  const dueDateLabel = useMemo(
    () => formatDueDate(debtor.periodo, locale, paymentWindowCloseDay),
    [debtor.periodo, locale, paymentWindowCloseDay]
  );

  const personName = contactName.trim() || debtor.nombreClinica;
  const isUsd = debtor.moneda === 'USD';
  const amountLabel = debtor.totalAdeudado.toLocaleString('es-AR');
  // Mismo texto que arma el backend: "$ 45.000" en pesos, "US$ 120" en WhatsApp y "$ 120 USD" en el email.
  const amountWhatsApp = `${isUsd ? 'US$' : '$'} ${amountLabel}`;
  const amountEmail = isUsd ? `${amountLabel} USD` : amountLabel;
  const concepto = formatSuscripcionConcepto(
    debtor.nombreClinica,
    debtor.periodo,
    debtor.planName
  );

  const previewText = useMemo(() => {
    if (channel === 'whatsapp') {
      return buildWhatsAppPreview({
        nombreCliente: personName,
        concepto,
        monto: amountWhatsApp,
        linkPago: LINK_PLACEHOLDER,
        contacto1: panelContacto1.trim() || t('pages.collections.contact.notDefined'),
        contacto2: panelContacto2.trim() || t('pages.collections.contact.notDefined'),
        telefono1: panelPhone1.trim() || t('pages.collections.contact.notDefined'),
        telefono2: panelPhone2.trim() || t('pages.collections.contact.notDefined'),
        dueDate: dueDateLabel,
      });
    }
    if (channel === 'email') {
      return buildEmailPreview({
        nombreCliente: personName,
        clinicName: debtor.nombreClinica,
        periodo: debtor.periodo,
        dueDate: dueDateLabel,
        monto: amountEmail,
        linkPago: LINK_PLACEHOLDER,
      });
    }
    return '';
  }, [
    channel,
    personName,
    concepto,
    amountWhatsApp,
    amountEmail,
    panelPhone1,
    panelPhone2,
    panelContacto1,
    panelContacto2,
    debtor.nombreClinica,
    debtor.periodo,
    dueDateLabel,
    t,
  ]);

  const emailSubject = t('pages.collections.contact.emailSubject', {
    clinic: debtor.nombreClinica,
    periodo: debtor.periodo,
  });

  if (!isOpen) return null;

  const createPaymentLink = async (): Promise<string> => {
    if (debtor.totalAdeudado <= 0) {
      throw new Error(t('pages.collections.invalidAmount'));
    }
    const tenantRef = debtor.tenantId || String(debtor.clinicaId);
    const [anio, mes] = debtor.periodo.split('-');
    const action = container.get<CreateMpPreferenceAction>(TYPES.CreateMpPreferenceAction);
    const result = await action.execute({
      title: `Cobranza ${debtor.nombreClinica} (deuda total)`,
      unitPrice: debtor.totalAdeudado,
      quantity: 1,
      externalReference: `TENANT-${tenantRef}-${anio}-${mes}-${Date.now()}`,
    });
    return result.initPoint;
  };

  const handleSend = async () => {
    if (!channel) return;

    if (!debtor.tenantId) {
      showError('Falta el tenantId de la clínica');
      return;
    }

    if (channel === 'email') {
      const to = email.trim();
      if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
        showError(t('pages.collections.contact.emailRequired'));
        return;
      }
    }

    if (channel === 'whatsapp') {
      const wa = normalizeWhatsAppPhone(phone);
      if (!wa) {
        showError(t('pages.collections.contact.phoneRequired'));
        return;
      }
    }

    setIsWorking(true);
    try {
      // En dólares no hay Mercado Pago: el backend pone el link a Suscripción en Coofit.
      let paymentLink = '';
      if (!isUsd) {
        success(t('pages.collections.creatingPreference'));
        paymentLink = await createPaymentLink();
      }

      await httpClient.post('/collections/contact-reminder', {
        channel,
        tenantId: debtor.tenantId,
        clinicaId: debtor.clinicaId,
        clinicName: debtor.nombreClinica,
        contactName: contactName.trim() || debtor.nombreClinica,
        planName: debtor.planName,
        periodo: debtor.periodo,
        amount: debtor.totalAdeudado,
        moneda: debtor.moneda ?? 'ARS',
        dueDateLabel,
        paymentLink,
        toEmail: email.trim() || null,
        toPhone: phone.trim() || null,
      });

      success(
        channel === 'email'
          ? t('pages.collections.contact.sentEmail', { email: email.trim() })
          : t('pages.collections.contact.sentWhatsApp', { phone: phone.trim() })
      );
      await loadHistory();
      onContactSaved?.();
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(
        axiosMsg ||
          (err instanceof Error ? err.message : t('pages.collections.paymentError'))
      );
    } finally {
      setIsWorking(false);
    }
  };

  const statusLabel = (status: string) =>
    t(`pages.collections.contact.status.${status}`, { defaultValue: status });

  return (
    <ModalShell
      isOpen={true}
      onClose={onClose}
      size="full"
      className="!max-w-5xl"
      title={t('pages.collections.contact.title')}
      subtitle={
        <>
          <p>{t('pages.collections.contact.subtitle', { clinic: debtor.nombreClinica })}</p>
          <p className="mt-1 text-xs">
            {t('pages.collections.contact.dueHint', {
              dueDate: dueDateLabel,
              amount: amountWhatsApp,
            })}
          </p>
        </>
      }
      bodyClassName="!p-0 !overflow-hidden"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isWorking} className="w-full sm:w-auto">
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!channel || isWorking || !debtor.tenantId}
            onClick={() => void handleSend()}
            className="w-full sm:w-auto"
          >
            {isWorking
              ? t('pages.collections.contact.sending')
              : t('pages.collections.contact.send')}
          </Button>
        </>
      }
    >
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2">
            <div className="p-5 space-y-4 overflow-y-auto border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-gray-700">
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-gray-600 dark:bg-gray-900/40">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t('pages.collections.contact.destination')}
                </p>
                <Input
                  label={t('pages.collections.contact.contactName')}
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder={debtor.nombreClinica}
                />
                <Input
                  label={t('pages.collections.contact.emailLabel')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@clinica.com"
                />
                {!email.trim() && (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {t('pages.collections.contact.emailMissing')}
                  </p>
                )}
                <Input
                  label={t('pages.collections.contact.phoneLabel')}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+54 9 11 1234-5678"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('pages.collections.contact.phoneHint')}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages.collections.contact.chooseChannel')}
                </p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setChannel('email')}
                    className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                      channel === 'email'
                        ? 'border-teal-500 bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200'
                        : 'border-slate-200 text-gray-700 hover:bg-slate-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    {t('pages.collections.contact.channelEmail')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannel('whatsapp')}
                    className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                      channel === 'whatsapp'
                        ? 'border-teal-500 bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200'
                        : 'border-slate-200 text-gray-700 hover:bg-slate-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    {t('pages.collections.contact.channelWhatsApp')}
                  </button>
                </div>
              </div>

              {channel === 'email' && (
                <p className="text-sm text-teal-800 dark:text-teal-200">
                  {t('pages.collections.contact.willSendEmail', {
                    email: email.trim() || t('pages.collections.contact.notDefined'),
                  })}
                </p>
              )}
              {channel === 'whatsapp' && (
                <p className="text-sm text-teal-800 dark:text-teal-200">
                  {t('pages.collections.contact.willSendWhatsApp', {
                    phone: phone.trim() || t('pages.collections.contact.notDefined'),
                  })}
                </p>
              )}

              <div className="rounded-lg border border-slate-200 dark:border-gray-600 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-gray-900/40 border-b border-slate-200 dark:border-gray-600">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t('pages.collections.contact.historyTitle')}
                  </p>
                  <button
                    type="button"
                    onClick={() => void loadHistory()}
                    className="text-[11px] text-teal-700 hover:underline dark:text-teal-300"
                    disabled={historyLoading || !debtor.tenantId}
                  >
                    {t('pages.collections.contact.historyRefresh')}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700">
                  {historyLoading ? (
                    <p className="px-3 py-4 text-xs text-gray-500">
                      {t('pages.collections.contact.historyLoading')}
                    </p>
                  ) : history.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-gray-500">
                      {t('pages.collections.contact.historyEmpty')}
                    </p>
                  ) : (
                    history.map((item) => (
                      <div key={item.id} className="px-3 py-2.5 text-xs space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-800 dark:text-gray-100">
                            {item.channel === 'email'
                              ? t('pages.collections.contact.historyChannelEmail')
                              : t('pages.collections.contact.historyChannelWhatsApp')}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              STATUS_COLORS[item.deliveryStatus] || STATUS_COLORS.pending
                            }`}
                          >
                            {statusLabel(item.deliveryStatus)}
                          </span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 truncate">
                          {item.recipient}
                          {item.periodo ? ` · ${item.periodo}` : ''}
                        </p>
                        <p className="text-gray-400 dark:text-gray-500">
                          {new Date(item.sentAt || item.createdAt).toLocaleString(locale)}
                          {item.sentByUsername
                            ? ` · ${t('pages.collections.contact.historyBy', {
                                user: item.sentByUsername,
                              })}`
                            : ''}
                        </p>
                        {item.errorMessage && (
                          <p className="text-red-600 dark:text-red-400 truncate">
                            {item.errorMessage}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="p-5 flex flex-col min-h-0 bg-slate-50/80 dark:bg-gray-900/30">
              <div className="flex items-center justify-between gap-2 mb-3 flex-shrink-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                  {t('pages.collections.contact.preview')}
                </p>
                {channel === 'whatsapp' && (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    recordatorio_pago_arg
                  </span>
                )}
              </div>

              {!channel ? (
                <div className="flex-1 flex items-center justify-center rounded-lg border border-dashed border-slate-300 dark:border-gray-600 bg-white/60 dark:bg-gray-800/40 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('pages.collections.contact.previewEmpty')}
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-slate-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
                  {channel === 'email' && (
                    <div className="px-3 py-2 border-b border-slate-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 flex-shrink-0">
                      <span className="font-medium">
                        {t('pages.collections.contact.previewSubject')}:
                      </span>{' '}
                      {emailSubject}
                    </div>
                  )}
                  <pre className="flex-1 min-h-0 overflow-y-auto px-4 py-3 text-sm whitespace-pre-wrap font-sans text-gray-800 dark:text-gray-100 leading-relaxed">
                    {previewText}
                  </pre>
                  <p className="px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-gray-900/40 border-t border-slate-200 dark:border-gray-700 flex-shrink-0">
                    {t('pages.collections.contact.previewLinkHint')}
                  </p>
                </div>
              )}
            </div>
          </div>
    </ModalShell>
  );
};
